import { VAPERITE_SELL_IN_APP_TAG } from './sellInAppTag';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Pagination, Paper,
  Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Block as ExcludeIcon,
  CheckCircle as ApproveIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';
import { darkTableSx, selectedChipProps } from './ui';
import StagedItemEditDialog, { StagedEditable } from './StagedItemEditDialog';
import NoWoocommerceDialog, { NO_WOOCOMMERCE_REASON } from './NoWoocommerceDialog';
import { stagingApiBase } from './stagingOrg';

// Staged items held back by a Missing Value Rule. Items stay here until someone
// approves them (flags are sticky: filling a gap does not release an item), so
// the tab is where gaps are filled — one by one with Edit, or in bulk through
// the xlsx export/import — and then approved, or excluded if it should never be sold
// (it moves to Excluded Products, where it can be un-excluded).

// brand: Jason, 29 Oct 2026 (Vend sent the item with no brand)
type FieldKey = 'description' | 'image' | 'price' | 'tags' | 'brand';

const FIELDS: FieldKey[] = ['description', 'image', 'price', 'tags', 'brand'];
const LABELS: Record<FieldKey, string> = {
  description: 'Description', image: 'Image', price: 'Price', tags: 'Tags', brand: 'Brand',
};
const PAGE_SIZE = 50;
/** Ticked rows per bulk-approve request (each one is a product creation, server-side in turn). */
const BULK_CHUNK = 10;

export interface FlaggedItem {
  sku: string;
  name: string;
  type: string | null;
  description: string | null;
  imageUrl: string | null;
  basePrice: number | null;
  tags: string[];
  brand: string | null;
  sellInApp: boolean;
  missingFields: FieldKey[];
  flaggedFields: FieldKey[];
}

type Counts = Record<FieldKey | 'ready', number>;

/** `sheet` is the workbook sheet the row is on ("Flagged Items", "Missing Image", ...). */
interface ImportSkip { sheet?: string; row: number; sku: string | null; reason: string }

const plural = (n: number) => `${n} item${n === 1 ? '' : 's'}`;
const formatPrice = (v: number | null) => (v == null ? '-' : `R${Number(v).toFixed(2)}`);

/** filename="…" from a Content-Disposition header, if there is one. */
function filenameFrom(disposition: unknown): string | null {
  const m = /filename="?([^";]+)"?/i.exec(String(disposition ?? ''));
  return m ? m[1] : null;
}

export default function FlaggedItemsTab({ onChanged }: { onChanged?: () => void }) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [items, setItems] = useState<FlaggedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState<FieldKey[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  const [busySku, setBusySku] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<StagedEditable | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [skipped, setSkipped] = useState<ImportSkip[] | null>(null);
  const [excludeRow, setExcludeRow] = useState<FlaggedItem | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  // A second Enter can land before the dialog closes: exclude once.
  const excluding = useRef(false);
  // Approve held by the server: no woocommerce tag in Vend — the person decides.
  const [noWooRow, setNoWooRow] = useState<FlaggedItem | null>(null);
  const [noWooBusy, setNoWooBusy] = useState(false);
  // Ready filter + tick-to-approve: only Ready rows (nothing missing) can be ticked, the
  // header box ticks every Ready row on the page, "Approve selected" bulk-approves them.
  const [readyOnly, setReadyOnly] = useState(false);
  // Fields an item may go live without (Missing Value Rules: "Can go live without it").
  // Ready = nothing missing outside these; their gaps still show, as grey chips.
  const [allowLive, setAllowLive] = useState<FieldKey[]>([]);
  const isReady = (row: FlaggedItem) => row.missingFields.every((f) => allowLive.includes(f));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // click SKU / Name / Type to sort (again = reverse); none = newest staged first
  const [sortBy, setSortBy] = useState<'sku' | 'name' | 'type' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const clickSort = (col: 'sku' | 'name' | 'type') => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  // Held by a bulk approve: no woocommerce tag in Vend — the person decides for the lot.
  const [noWooBulk, setNoWooBulk] = useState<Array<{ sku: string; name: string }>>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  // Field order matches the API and the xlsx, whatever order the chips were clicked in.
  const filterParams = useCallback(() => ({
    missing: missing.length > 0 ? FIELDS.filter((f) => missing.includes(f)).join(',') : undefined,
    search: search || undefined,
    ready: readyOnly ? 'true' : undefined,
    sort: sortBy ?? undefined,
    dir: sortBy ? sortDir : undefined,
  }), [missing, search, readyOnly, sortBy, sortDir]);

  const load = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/flagged`, {
        params: { ...filterParams(), page, limit: PAGE_SIZE },
      });
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setCounts(data.counts ?? null);
      setAllowLive(data.allowLive ?? []);
      setSelected(new Set()); // ticks are per page and per filter
    } catch (err: any) {
      notify(`Failed to load flagged items: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, notify]);

  useEffect(() => { load(); }, [load]);

  // Debounce typing into the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // The latest load, for code that finishes after a re-render (the bulk approve).
  const loadRef = useRef(load);
  loadRef.current = load;

  // Ticks belong to the page and filter they were made on: clear them the moment either
  // changes, not when the new page arrives, so a click in between can't approve unseen rows.
  const goToPage = (p: number) => { setSelected(new Set()); setPage(p); };

  // A missing-field chip and Ready exclude each other: Ready means nothing is missing.
  const toggleFilter = (field: FieldKey) => {
    setReadyOnly(false);
    setMissing((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
    goToPage(1);
  };
  const toggleReady = () => {
    setMissing([]);
    setReadyOnly((r) => !r);
    goToPage(1);
  };

  // Approved and excluded items leave Flagged Items: drop the rows and their share of the
  // counts rather than refetching the page under the reviewer's cursor.
  const dropRows = (rows: FlaggedItem[]) => {
    if (rows.length === 0) return;
    const gone = new Set(rows.map((r) => r.sku));
    setItems((prev) => prev.filter((i) => !gone.has(i.sku)));
    setSelected((prev) => new Set(Array.from(prev).filter((s) => !gone.has(s))));
    setTotal((t) => Math.max(0, t - rows.length));
    setCounts((c) => {
      if (!c) return c;
      const next = { ...c };
      rows.forEach((row) => {
        row.missingFields.forEach((f) => { next[f] = Math.max(0, next[f] - 1); });
        if (isReady(row)) next.ready = Math.max(0, next.ready - 1);
      });
      return next;
    });
    onChanged?.();
  };
  const dropRow = (row: FlaggedItem) => dropRows([row]);

  const readyRows = items.filter(isReady);
  const toggleSelected = (sku: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(sku)) next.delete(sku); else next.add(sku);
    return next;
  });
  const allReadyTicked = readyRows.length > 0 && readyRows.every((r) => selected.has(r.sku));
  const toggleAllReady = () => setSelected(allReadyTicked ? new Set() : new Set(readyRows.map((r) => r.sku)));

  /**
   * Approve the ticked rows through bulk-approve, BULK_CHUNK at a time: the server creates
   * them one after another, so a whole page in one request could outlast the proxy timeout.
   * Paging, filters and row buttons are disabled meanwhile (bulkBusy).
   */
  const handleBulkApprove = async (skus: string[], allowNoWoocommerce = false) => {
    if (skus.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    type Result = { sku: string; success: boolean; error?: string; noWoocommerce?: boolean };
    const results: Result[] = [];
    const held: Array<{ sku: string; name: string }> = [];
    let error: string | null = null;
    try {
      for (let i = 0; i < skus.length; i += BULK_CHUNK) {
        const chunk = skus.slice(i, i + BULK_CHUNK);
        let data: any;
        try {
          // eslint-disable-next-line no-await-in-loop
          ({ data } = await authInstance.post(`${stagingApiBase()}/staging/items/bulk-approve`,
            allowNoWoocommerce ? { skus: chunk, allowNoWoocommerce } : { skus: chunk }));
        } catch (err: any) {
          // 422 = every one in the chunk refused; the body still lists why (and any held).
          data = err.response?.data;
          if (!data?.results) { error = data?.error || err.message; break; }
        }
        results.push(...(data.results ?? []));
        held.push(...(data.noWoocommerce ?? []));
      }
    } finally {
      const approved = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success && !r.noWoocommerce);
      const stopped = error ? ` (stopped: ${error})` : '';
      notify(
        `Approved ${plural(approved)}${held.length ? `, ${held.length} waiting on the ${VAPERITE_SELL_IN_APP_TAG} question` : ''}${failed.length ? `, ${failed.length} failed: ${failed[0].error ?? 'see the item'}` : ''}${stopped}`,
        { type: failed.length || error ? 'warning' : 'success' },
      );
      if (approved > 0) onChanged?.();
      if (held.length > 0) setNoWooBulk(held);
      setBulkBusy(false);
      // Always reload: brings the next batch up (approve 50, the next 50 appear, ...) and
      // shows the truth even if a request died after the server had already approved some.
      loadRef.current();
    }
  };

  const handleApprove = async (row: FlaggedItem, allowNoWoocommerce = false) => {
    setBusySku(row.sku);
    try {
      await authInstance.post(`${stagingApiBase()}/staging/items/${row.sku}/approve`, allowNoWoocommerce ? { allowNoWoocommerce } : {});
      notify(`${row.sku} approved`, { type: 'success' });
      dropRow(row);
    } catch (err: any) {
      if (err.response?.data?.noWoocommerce) {
        setNoWooRow(row);
        return;
      }
      notify(`Failed to approve: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusySku(null);
    }
  };

  const handleExclude = async (preset?: { row: FlaggedItem; reason: string }) => {
    const row = preset?.row ?? excludeRow;
    const reason = (preset?.reason ?? excludeReason).trim();
    if (!row || !reason || excluding.current) return;
    excluding.current = true;
    setExcludeRow(null);
    setBusySku(row.sku);
    try {
      await authInstance.post(`${stagingApiBase()}/staging/items/${row.sku}/reject`, { reason });
      notify(`${row.sku} excluded — it is in Excluded Products`, { type: 'success' });
      dropRow(row);
    } catch (err: any) {
      notify(`Failed to exclude: ${err.response?.data?.error || err.message}`, { type: 'error' });
      // 409: it was approved or excluded elsewhere — show the list as it is now.
      if (err.response?.status === 409) load();
    } finally {
      excluding.current = false;
      setBusySku(null);
    }
  };

  // The list row lacks the barcode, category and 18+ flag, and the dialog PATCHes every
  // field — so load the full staged row first, or Save would clear what it did not show.
  const handleOpenEdit = async (row: FlaggedItem) => {
    setBusySku(row.sku);
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/items/${row.sku}`);
      const item = data.item;
      setEditItem({
        sku: item.sku,
        type: item.type ?? 'new_item',
        name: item.suggestedName ?? row.name,
        description: item.suggestedDescription ?? null,
        basePrice: item.basePrice ?? null,
        category: item.categories?.[0]?.payloadCategoryName ?? null,
        tags: item.webhookTags ?? null,
        brand: item.canonicalItemData?.brand ?? row.brand ?? null,
        gtin: item.gtin ?? null,
        imageUrl: item.suggestedImageUrl ?? null,
        sellInApp: item.sellInApp !== false,
        isRestricted: item.isRestricted === true,
        sellInAppOverridden: item.sellInAppOverridden === true,
      });
    } catch (err: any) {
      notify(`Failed to load ${row.sku}: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusySku(null);
    }
  };

  const handleSaved = () => {
    load();
    onChanged?.();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authInstance.get(`${stagingApiBase()}/staging/flagged/export.xlsx`, {
        params: filterParams(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(res.headers?.['content-disposition']) ?? 'flagged-items.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      notify(`Export failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // No JSON Content-Type: the browser sets the multipart boundary.
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/flagged/import`, form, {
        headers: { 'Content-Type': undefined },
      });
      const skips: ImportSkip[] = data?.skipped ?? [];
      notify(`Updated ${plural(data?.updated ?? 0)}, skipped ${skips.length}`, {
        type: skips.length > 0 ? 'warning' : 'success',
      });
      if (skips.length > 0) setSkipped(skips);
      load();
      onChanged?.();
    } catch (err: any) {
      // A blob/JSON error body from the import comes back as { error }.
      notify(`Import failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Paper ref={listTopRef} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              Flagged Items
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              Held by a Missing Value Rule. Fill the gaps here or in the xlsx, then approve.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {selected.size > 0 && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleBulkApprove(Array.from(selected))}
                disabled={bulkBusy}
              >
                {bulkBusy ? 'Approving…' : `Approve selected (${selected.size})`}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Export xlsx
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={importing}
            >
              {importing ? 'Importing…' : 'Import xlsx'}
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              data-testid="flagged-import-input"
              onChange={handleImport}
            />
          </Stack>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mt: 2 }}>
          <TextField
            size="small"
            placeholder="Search SKU or name"
            value={searchInput}
            disabled={bulkBusy}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              },
            }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {FIELDS.map((field) => {
              const active = missing.includes(field);
              return (
                <Chip
                  key={field}
                  label={`${LABELS[field]} (${counts?.[field] ?? 0})`}
                  onClick={() => toggleFilter(field)}
                  disabled={bulkBusy}
                  color={active ? 'warning' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  {...selectedChipProps(active)}
                  size="small"
                />
              );
            })}
            {/* Filter: flagged items with every gap filled, which only need approving. */}
            <Tooltip title={`Show only items with nothing blocking: ready to approve${allowLive.length ? ` (may still lack: ${allowLive.map((f) => LABELS[f].toLowerCase()).join(', ')})` : ''}`}>
              <Chip
                label={`Ready (${counts?.ready ?? 0})`}
                onClick={toggleReady}
                disabled={bulkBusy}
                color="success"
                variant={readyOnly ? 'filled' : 'outlined'}
                {...selectedChipProps(readyOnly)}
                size="small"
              />
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            No flagged items{missing.length > 0 || search || readyOnly ? ' match these filters' : ''}.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={darkTableSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Tooltip title={readyRows.length ? 'Tick every Ready item on this page' : 'No Ready items on this page'}>
                    <span>
                      <Checkbox
                        size="small"
                        inputProps={{ 'aria-label': 'Tick every Ready item on this page' }}
                        disabled={readyRows.length === 0 || bulkBusy}
                        checked={allReadyTicked}
                        indeterminate={selected.size > 0 && !allReadyTicked}
                        onChange={toggleAllReady}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                {(['sku', 'name', 'type'] as const).map((col) => (
                  <TableCell key={col} sortDirection={sortBy === col ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === col}
                      direction={sortBy === col ? sortDir : 'asc'}
                      onClick={() => clickSort(col)}
                      disabled={bulkBusy}
                    >
                      {col === 'sku' ? 'SKU' : col === 'name' ? 'Name' : 'Type'}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Missing</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.sku} selected={selected.has(row.sku)}>
                  <TableCell padding="checkbox">
                    {/* Only Ready rows can be ticked: anything still missing a value would be refused. */}
                    <Checkbox
                      size="small"
                      inputProps={{ 'aria-label': `Tick ${row.sku}` }}
                      disabled={!isReady(row) || bulkBusy}
                      checked={selected.has(row.sku)}
                      onChange={() => toggleSelected(row.sku)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.sku}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={{ color: `${colors.textSecondary} !important` }}>{row.type ?? '-'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {isReady(row) && <Chip label="Ready" color="success" size="small" />}
                      {FIELDS.filter((f) => row.missingFields.includes(f)).map((f) => (allowLive.includes(f) ? (
                        // A gap it may go live with (description): shown, but grey, not a blocker.
                        <Tooltip key={f} title={`No ${LABELS[f].toLowerCase()}: allowed to go live without it`}>
                          <Chip label={`No ${LABELS[f].toLowerCase()}`} variant="outlined" size="small" />
                        </Tooltip>
                      ) : (
                        <Chip key={f} label={LABELS[f]} color="warning" variant="outlined" size="small" />
                      )))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPrice(row.basePrice)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Edit"
                          disabled={busySku === row.sku || bulkBusy}
                          onClick={() => handleOpenEdit(row)}
                          sx={{ color: colors.info }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Exclude: never sell this item">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Exclude"
                          disabled={busySku === row.sku || bulkBusy}
                          onClick={() => { setExcludeReason(''); setExcludeRow(row); }}
                          sx={{ color: colors.error }}
                        >
                          <ExcludeIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Approve">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Approve"
                          disabled={busySku === row.sku || bulkBusy}
                          onClick={() => handleApprove(row)}
                          sx={{ color: colors.success }}
                        >
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => goToPage(p)} disabled={bulkBusy} size="small" />
        </Box>
      )}

      <StagedItemEditDialog
        open={!!editItem}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={handleSaved}
      />

      <Dialog
        open={!!excludeRow}
        onClose={() => setExcludeRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Exclude {excludeRow?.sku}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            {excludeRow?.name} will not become a product. It moves to Excluded Products, where it can be un-excluded.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Reason"
            placeholder="e.g. Not sold online"
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleExclude(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcludeRow(null)} sx={{ color: colors.textSecondary }}>Cancel</Button>
          <Button onClick={() => handleExclude()} disabled={!excludeReason.trim()} color="error" variant="contained">
            Exclude
          </Button>
        </DialogActions>
      </Dialog>

      <NoWoocommerceDialog
        open={!!noWooRow}
        items={noWooRow ? [{ sku: noWooRow.sku, name: noWooRow.name }] : []}
        busy={noWooBusy}
        onSkip={() => setNoWooRow(null)}
        onConfirm={async () => {
          const row = noWooRow!;
          setNoWooBusy(true);
          await handleApprove(row, true);
          setNoWooBusy(false);
          setNoWooRow(null);
        }}
        onExclude={async () => {
          const row = noWooRow!;
          setNoWooRow(null);
          await handleExclude({ row, reason: NO_WOOCOMMERCE_REASON });
        }}
      />

      <NoWoocommerceDialog
        open={noWooBulk.length > 0}
        items={noWooBulk}
        busy={bulkBusy}
        onSkip={() => setNoWooBulk([])}
        onConfirm={async () => {
          const skus = noWooBulk.map((i) => i.sku);
          setNoWooBulk([]);
          await handleBulkApprove(skus, true);
        }}
        onExclude={async () => {
          // One bulk-reject for the lot, by SKU: the page may have reloaded since they were held.
          const skus = noWooBulk.map((h) => h.sku);
          setNoWooBulk([]);
          setBulkBusy(true);
          try {
            await authInstance.post(`${stagingApiBase()}/staging/items/bulk-reject`, { skus, reason: NO_WOOCOMMERCE_REASON });
            notify(`Excluded ${plural(skus.length)}: they are in Excluded Products`, { type: 'success' });
            onChanged?.();
          } catch (err: any) {
            notify(`Failed to exclude: ${err.response?.data?.error || err.message}`, { type: 'error' });
          } finally {
            setBulkBusy(false);
            loadRef.current();
          }
        }}
      />

      <Dialog
        open={!!skipped}
        onClose={() => setSkipped(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Skipped rows</DialogTitle>
        <DialogContent>
          <TableContainer sx={darkTableSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(skipped ?? []).map((s, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`${s.row}-${i}`}>
                    <TableCell>{s.sheet ? `${s.sheet} · ${s.row}` : s.row}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{s.sku ?? '-'}</TableCell>
                    <TableCell>{s.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipped(null)} sx={{ color: colors.textSecondary }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
